<script>
  import { setContext } from 'svelte';
  import '@awesome.me/webawesome-pro/dist/components/page/page.js';

  import Header from './components/layout/Header.svelte';
  import Sidebar from './components/layout/Sidebar.svelte';
  import LoadSpinner from '#components/LoadSpinner';
  import AssetGrid from '#components/AssetGrid';
  import AssetDetailModal from '#components/AssetDetailModal';

  import { initCategories } from '#state/categories';
  import { authState, initAuth, getFlag, apiFetch } from '#state/auth';

  let detailModal = $state(null);
  let initialHashHandled = false;

  // Expose the modal trigger to all deeply nested child components
  setContext('openAssetModal', (id) => {
    if (detailModal !== null) {
      detailModal.show(id);
    }
  });

  const randomWords = [
    'Ultimate', 'Manager', 'Controller', 'System', 'Toolkit', 'Helper',
    'Essentials', 'Pro', 'Framework', 'Extension', 'Node'
  ];

  function getRandomTitle(baseTitle) {
    const word1 = randomWords[Math.floor(Math.random() * randomWords.length)];
    const word2 = randomWords[Math.floor(Math.random() * randomWords.length)];
    return `${baseTitle} - ${word1} ${word2}`;
  }

  async function fetchAssets() {
    // Artificial 1000ms delay to test the LoadSpinner
    // await new Promise(resolve => setTimeout(resolve, 1000));

    const res = await apiFetch('/asset');
    if (res.ok === false) {
      throw new Error(`Failed to fetch assets: ${res.statusText}`);
    }

    const data = await res.json();

    // Accommodate array responses directly or nested envelopes
    const baseList = Array.isArray(data) === true ? data : (data.result || []);

    // Utilizing the new clean helper method
    const debugLevel = getFlag('debug_level', 0);

    // Only duplicate if logged in and debug_level is 3 or higher
    if (authState.isAuthenticated === true && debugLevel >= 3) {
      let testingList = [];
      for (let i = 0; i < 12; i++) {
        for (let j = 0; j < baseList.length; j++) {
          let duplicate = Object.assign({}, baseList[j]);
          duplicate._dom_key = 'test_' + i + '_' + j;
          duplicate.title = getRandomTitle(duplicate.title);
          duplicate.category_id = Math.floor(Math.random() * 10) + 1;
          testingList.push(duplicate);
        }
      }
      return testingList;
    }

    // Otherwise, just return the exact items from the backend
    return baseList;
  }

  async function initializeApp() {
    // Run both the auth check and the category fetch concurrently before rendering
    await Promise.all([
      initCategories(),
      initAuth()
    ]);
  }

  $effect(() => {
    // 1. Synchronously read detailModal so Svelte tracks it as a dependency.
    // If it is null (because the outer spinner is still loading), we bail.
    // Svelte will automatically re-execute this effect the exact millisecond it binds!
    if (detailModal === null || initialHashHandled === true) {
      return;
    }

    const hash = window.location.hash;
    if (hash !== null && hash !== undefined && hash.length > 1) {
      const id = hash.substring(1);
      initialHashHandled = true;

      // A tiny delay ensures the native WebAwesome dialog has finished painting
      setTimeout(() => {
        detailModal.show(id);
      }, 50);
    } else {
      // If there is no hash on load, mark it handled so we don't try later
      initialHashHandled = true;
    }
  });
</script>

<LoadSpinner loader={initializeApp}>
  {#snippet children()}
    <wa-page>
      <div slot="header">
        <Header />
      </div>
      <div slot="navigation">
        <Sidebar />
      </div>
      <div class="app-main">
        <LoadSpinner loader={fetchAssets}>
          {#snippet children(assets)}
            <AssetGrid {assets} />
          {/snippet}
        </LoadSpinner>
      </div>

      <AssetDetailModal bind:this={detailModal} />
    </wa-page>
  {/snippet}
</LoadSpinner>

<style>
  .app-main {
    padding: 2rem;
    width: 100%;
    overflow-y: auto;
  }
</style>